/**
 * `/population` — the installed-population register. Port of the demo's
 * `renderPopulation()` (line 2804) and `exportPopulation()` (line 2809).
 *
 * Every serial number Meridian has in the field, with its hours, where it is,
 * who operates it, what it is doing, when it was last heard from, its condition
 * band and its hot spots. This is the list the original aftermarket programme
 * kept by hand; it is now the fleet register joined to the condition register
 * and, for the twelve connected surface drills, to the RMS feed.
 *
 * ## Data
 *
 * The eager fleet and condition tables, plus one lazy domain — `rms` — for the
 * live drill status. The page renders without RMS and fills those twelve rows
 * in when it lands.
 *
 * ## Declared deviation
 *
 * The demo's Export CSV always writes the whole hundred whatever is filtered on
 * screen. This writes the rows in view. The header and column order are the
 * demo's, so the two files are diffable.
 *
 * @see src/versions/v1/modules/service/PopulationScene.transform.ts
 * @see docs/logic/workflows/population.md
 */

import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Download, ListFilter } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  Input,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { FLEET, MINES, MINE_ORDER } from '../../data/core';
import type { ConditionBand, MachineClass, MineCode, ModelCode } from '../../schema/machine';
import { CONDITION_BANDS } from '../../schema/machine';
import { CLSNAME, CLS_ORDER, groupNum } from '../../shared/oem-engine';
import { SourceLink } from '../../shared/oem-tiles';
import { useToast } from '../../shared/toast/ToastContext';
import { useOemData } from '../../shared/useOemData';
import { useRowsDrawer } from './source-slice';
import {
  CSV_FILENAME,
  EMPTY_FILTER,
  filterRows,
  isPopulationSort,
  populationCsv,
  populationModels,
  populationRows,
  populationSummary,
  sortRows,
  type PopulationFilter,
  type PopulationRow,
  type PopulationSort,
} from './PopulationScene.transform';

/** Demo line 2806. The page's standfirst, verbatim. */
const STANDFIRST =
  'Every serial number in the population — activity, location, operator, hours, bridging units ' +
  'and hot spots — kept current from the fleet register, the RMS and live feeds and the ' +
  'condition register. This is the list the original programme kept by hand.';

export function PopulationScene() {
  const [params, setParams] = useSearchParams();
  const rms = useOemData('rms');
  const { show } = useToast();
  const openRows = useRowsDrawer();

  const filter = useMemo<PopulationFilter>(
    () => ({
      q: params.get('q') ?? EMPTY_FILTER.q,
      mine: (params.get('mine') ?? '') as MineCode | '',
      cls: (params.get('cls') ?? '') as MachineClass | '',
      model: (params.get('model') ?? '') as ModelCode | '',
      band: (params.get('band') ?? '') as ConditionBand | '',
    }),
    [params],
  );

  const sortKey = params.get('sort');
  const sort: PopulationSort | null = isPopulationSort(sortKey) ? sortKey : null;
  const descending = params.get('dir') === 'desc';

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const toggleSort = useCallback(
    (key: PopulationSort) => {
      const next = new URLSearchParams(params);
      if (sort === key && !descending) next.set('dir', 'desc');
      else next.delete('dir');
      next.set('sort', key);
      setParams(next, { replace: true });
    },
    [params, setParams, sort, descending],
  );

  const all = useMemo(
    () => (rms.data ? populationRows({ rmsStatus: rms.data.rms_status }) : []),
    [rms.data],
  );
  const rows = useMemo(
    () => sortRows(filterRows(all, filter), sort, descending),
    [all, filter, sort, descending],
  );
  const summary = useMemo(() => populationSummary(rows, all), [rows, all]);
  const models = useMemo(() => populationModels(), []);

  const exportCsv = useCallback(() => {
    const blob = new Blob([populationCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = CSV_FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoking in the same tick can cancel the download before the browser has
    // read the blob; the demo waits 1.5 s and so does this.
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
    show(`${rows.length} serials exported to ${CSV_FILENAME}`);
  }, [rows, show]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="PopulationScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-6" as="div">
            <SurfacePageHeader
              kicker="Workflows"
              title="Population list"
              description={STANDFIRST}
              actions={
                <div className="flex items-center gap-2">
                  <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                    {summary.shown} of {summary.total} serials · {summary.bridging} bridging units ·{' '}
                    {summary.hotSpots} hot spots
                  </span>
                  <HelpAnchor doc="workflows/population" />
                </div>
              }
            />

            <AskStrip view="population" />

            <Commentable name="PopulationFilters">
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor="pop-q">
                  Search the register
                </label>
                <Input
                  id="pop-q"
                  value={filter.q}
                  placeholder="Search serial, model, site…"
                  onChange={(e) => setParam('q', e.target.value)}
                  className="max-w-[240px]"
                />
                <FilterSelect
                  id="pop-mine"
                  label="Site"
                  value={filter.mine}
                  onChange={(v) => setParam('mine', v)}
                  anyLabel="All sites"
                  options={MINE_ORDER.map((m) => [m, MINES[m].name])}
                />
                <FilterSelect
                  id="pop-cls"
                  label="Class"
                  value={filter.cls}
                  onChange={(v) => setParam('cls', v)}
                  anyLabel="All classes"
                  options={CLS_ORDER.map((c) => [c, CLSNAME[c]])}
                />
                <FilterSelect
                  id="pop-model"
                  label="Model"
                  value={filter.model}
                  onChange={(v) => setParam('model', v)}
                  anyLabel="All models"
                  options={models.map((m) => [m, m])}
                />
                <FilterSelect
                  id="pop-band"
                  label="Condition"
                  value={filter.band}
                  onChange={(v) => setParam('band', v)}
                  anyLabel="Any condition"
                  options={CONDITION_BANDS.map((b) => [b.key, b.label])}
                />
                <button
                  type="button"
                  onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                  style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  Export CSV
                </button>
                <SourceLink onClick={() => openRows('fleet', FLEET)}>fleet register</SourceLink>
              </div>
            </Commentable>

            {rms.error ? (
              <InlineError>{`The RMS feed did not load: ${rms.error.message}`}</InlineError>
            ) : !rms.data ? (
              <SkeletonRows count={12} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<ListFilter className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
                title="No serials match"
                description="Clear a filter to widen the register."
              />
            ) : (
              <Commentable name="PopulationRegister">
                <Table className="min-w-[1280px]">
                  <THead>
                    <TR>
                      <SortHeader
                        column="serial"
                        label="Serial"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <SortHeader
                        column="machine"
                        label="Machine"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <SortHeader
                        column="model"
                        label="Model"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <SortHeader
                        column="site"
                        label="Site · location"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <TH>Operated by</TH>
                      <SortHeader
                        column="hours"
                        label="Hours"
                        align="right"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <SortHeader
                        column="commissioned"
                        label="Commissioned"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <TH>Activity</TH>
                      <TH>Last heard</TH>
                      <SortHeader
                        column="condition"
                        label="Condition"
                        sort={sort}
                        descending={descending}
                        onSort={toggleSort}
                      />
                      <TH>Hot spots</TH>
                      <TH>Bridging</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map((row) => (
                      <RegisterRow key={row.machine.machine_id} row={row} />
                    ))}
                  </TBody>
                </Table>
              </Commentable>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function RegisterRow({ row }: { row: PopulationRow }) {
  const f = row.machine;
  return (
    <TR>
      <TD className="mono whitespace-nowrap text-[11px]">{f.serial}</TD>
      <TD>
        <Link
          to={`/machines/${f.machine_id}`}
          className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
          style={{ color: 'var(--accent)' }}
        >
          {f.machine_id}
        </Link>
      </TD>
      <TD className="mono whitespace-nowrap">{f.model}</TD>
      <TD className="whitespace-nowrap text-[11.5px]">
        {row.siteShort} · {row.location}
      </TD>
      <TD className="whitespace-nowrap text-[11.5px]">
        {f.operated_by.replace(' Contracting', '').replace(' Services', '')}
      </TD>
      <TD className="mono text-right tabular-nums">{groupNum(f.engine_hours)}</TD>
      <TD className="mono whitespace-nowrap text-[11px]">{f.commissioned}</TD>
      <TD className="whitespace-nowrap text-[11.5px]">{row.activity}</TD>
      <TD className="mono whitespace-nowrap text-[11px]">{row.lastHeard}</TD>
      <TD>
        <span className={`band-pill band-${row.band}`}>{row.bandLabel}</span>
      </TD>
      <TD className="text-[11.5px]">
        {row.hotSpots.length
          ? row.hotSpots.map((h) => (
              <span key={h.component} className="block whitespace-nowrap">
                {h.component} <b className="mono">{h.health_score}</b>
              </span>
            ))
          : '—'}
      </TD>
      <TD className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
        {row.bridging ?? ''}
      </TD>
    </TR>
  );
}

/** A column header that sorts. Ascending first, then descending, both on the URL. */
function SortHeader({
  column,
  label,
  align = 'left',
  sort,
  descending,
  onSort,
}: {
  column: PopulationSort;
  label: string;
  align?: 'left' | 'right';
  sort: PopulationSort | null;
  descending: boolean;
  onSort: (column: PopulationSort) => void;
}) {
  const active = sort === column;
  const Icon = descending ? ArrowDown : ArrowUp;
  return (
    <TH aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex w-full items-center gap-1 whitespace-nowrap rounded uppercase tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] ${
          align === 'right' ? 'justify-end' : ''
        }`}
        style={{ color: active ? 'var(--ink-primary)' : undefined }}
      >
        {label}
        {active ? <Icon className="h-3 w-3" aria-hidden /> : null}
      </button>
    </TH>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  anyLabel,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  anyLabel: string;
  options: readonly (readonly [string, string])[];
}) {
  return (
    <>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border px-2.5 py-1.5 text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
        style={{
          borderColor: 'var(--hairline)',
          background: 'var(--surface-2)',
          color: 'var(--ink-primary)',
        }}
      >
        <option value="">{anyLabel}</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </>
  );
}
